import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { GlobalContext } from "../GlobalContext";
import NavBar from "../partials/NavBar";
import Editor from "@monaco-editor/react"; // Import Monaco Editor
import DOMPurify from "dompurify";

// Term text -> Monaco snippet text. Snippet syntax treats "$name" as a variable
// (an unknown one is replaced by its bare name, so "$s2" would become "s2") and
// "$1" as a tab stop; L2 variables like $s1 must therefore be escaped, while each
// standalone "$" placeholder becomes a tab stop the caret jumps to.
function toTermSnippet(text) {
  let n = 0;
  return String(text || "")
    .replace(/\\/g, "\\\\")
    .replace(/\}/g, "\\}")
    .replace(/\$(?=\w)/g, "\\$")
    .replace(/\$(?!\w)/g, () => `\${${++n}:}`);
}

// Language selector ("en"/"ru"/"kr") -> server model code / full name.
const OUT_CODE = { en: "EU", ru: "RU", kr: "KR" };
const OUT_NAME = { en: "English", ru: "Russian", kr: "Korean" };
const detectLang = (t) =>
  /[가-힣]/.test(t) ? "KR" : /[а-яА-Я]/.test(t) ? "RU" : /[a-zA-Z]/.test(t) ? "EU" : null;

const TransTool = () => {
  const { socket, isSocketReady, username, logout, toast, updateDatabase, versions } =
    useContext(GlobalContext);

  const [inputValue, setInputValue] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [outputValue, setOutputValue] = useState("");
  const [rawValue, setRawValue] = useState("");
  const [preview, setPreview] = useState("");
  const [searchType, setSearchType] = useState("skill");
  const [langType, setLangType] = useState("en");
  const [prefixType, setPrefixType] = useState("");
  const [usePrefix, setUsePrefix] = useState(true);
  const [timer, setTimer] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [monacoEditor, setMonacoEditor] = useState(null); // Reference to the Monaco Editor instance
  const [suggestions, setSuggestions] = useState([]);
  const debounceTimeoutRef = useRef(null);
  const [currentTranslationSuggestions, setcurrentTranslationSuggestions] = useState([])
  const [suggestBox, setSuggestBox] = useState(null); // inline translation suggestions
  const suggestTimerRef = useRef(null);
  const socketRef = useRef(null); // current socket (editor handlers capture a stale one)
  const versionsRef = useRef([]);
  const langTypeRef = useRef("en");
  const lookupIdRef = useRef(0);

  // Reference to the Monaco Editor instance
  const editorRef = useRef(null);
  const prefixRef = useRef("");
  // Kept in sync with state so the Monaco Ctrl+C command (registered once at
  // mount) always reads the CURRENT values instead of a stale closure.
  const usePrefixRef = useRef(true);
  const searchTypeRef = useRef("skill");

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu({
      x: event.pageX,
      y: event.pageY,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  useEffect(() => {
    prefixRef.current = prefixType;
  }, [prefixType]);

  useEffect(() => {
    usePrefixRef.current = usePrefix;
  }, [usePrefix]);

  useEffect(() => {
    searchTypeRef.current = searchType;
  }, [searchType]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    versionsRef.current = versions || [];
  }, [versions]);

  useEffect(() => {
    langTypeRef.current = langType;
  }, [langType]);

  useEffect(() => {
    setSearchType("skill");
    setPrefixType("desc");
    setLangType("en");
  }, []);

  useEffect(() => {
    requestTerms();
  }, [isSocketReady]);

  useEffect(() => {
    switch(searchType) {
      case "skill":
        document.getElementById("typeselect").value = "skill";
        break;
      case "item":
        document.getElementById("typeselect").value = "item";
        break;
      case "string":
        document.getElementById("typeselect").value = "string";
        break;
      default:
        document.getElementById("typeselect").value = "other";
        break;
    }
  }, [searchType]);

  useEffect(() => {
    if (!socket || !isSocketReady) {
      return;
    }

    const handleTerms = (data) => {      
        const newSuggestions = data.map((item) => ({
            label: item,
            insertText: item,
            kind: monacoEditor?.languages.CompletionItemKind.Keyword || 14, 
            insertTextRules: monacoEditor?.languages.CompletionItemInsertTextRule.InsertAsSnippet || 4,
            filterText: item,
            range: undefined
          }));
      
        setSuggestions(newSuggestions);
      };

    // Register the event listener
    socket.on("terms", handleTerms);

    // Cleanup function to remove the listener
    return () => {
      socket.off("terms", handleTerms);
    };
  }, [socket, isSocketReady]);

  function hexToRGBA(hex) {
    hex = hex.replace("#", "");
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { red: r / 255, green: g / 255, blue: b / 255, alpha: 1 };
  }
  
  function rgbaToHex(color) {
    const r = Math.round(color.red * 255);
    const g = Math.round(color.green * 255);
    const b = Math.round(color.blue * 255);
    return `${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
  }
  

  useEffect(() => {
  if (!monacoEditor || suggestions.length === 0) return;

  /*monacoEditor.languages.registerColorProvider("myCustomLanguage", {
    provideDocumentColors: function (model) {
      const matches = [];
      const regex = /<font\s+color="?(#?[A-Fa-f0-9]{6})"/g;
      let match;
  
      while ((match = regex.exec(model.getValue())) !== null) {
        const start = match.index + match[0].indexOf(match[1]);
        const end = start + match[1].length;
        const range = new monacoEditor.Range(1, start + 1, 1, end + 1);
  
        matches.push({
          color: hexToRGBA(match[1]),
          range: range
        });
      }
  
      return matches;
    },
  
    provideColorPresentations: function (model, color, token) {
      const hex = rgbaToHex(color.color);
      console.log("Color:", hex);
      return [{ label: hex }];
    }
  });*/

  if (window.myCompletionProvider) {
    window.myCompletionProvider.dispose();
  }

  const colorSuggestions = [
    { label: "⬜ FFFFFF", insertText: "FFFFFF", detail: "White", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "⬜ DDDDDD", insertText: "DDDDDD", detail: "Light Grey", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟫 BBAA88", insertText: "BBAA88", detail: "Beige Brown", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟫 FED7A0", insertText: "FED7A0", detail: "Peach", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

{ label: "🟨 FFFB99", insertText: "FFFB99", detail: "Pale Yellow", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟨 EDE313", insertText: "EDE313", detail: "Lemon Yellow", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟨 FFFF00", insertText: "FFFF00", detail: "Pure Yellow", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟨 FFBB00", insertText: "FFBB00", detail: "Golden Yellow", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟨 EFFF04", insertText: "EFFF04", detail: "Neon Yellow", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

{ label: "🟧 EEAA22", insertText: "EEAA22", detail: "Dark Orange", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟧 FF9955", insertText: "FF9955", detail: "Peachy Orange", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟧 FF8800", insertText: "FF8800", detail: "Pumpkin Orange", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟧 FF8000", insertText: "FF8000", detail: "Bright Orange", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

{ label: "🟥 FF6666", insertText: "FF6666", detail: "Soft Red", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟥 FF0000", insertText: "FF0000", detail: "Red", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟪 F00FFF", insertText: "F00FFF", detail: "Magenta", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟪 C270F6", insertText: "C270F6", detail: "Purple", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟪 C170CA", insertText: "C170CA", detail: "Lavender", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟪 B200ED", insertText: "B200ED", detail: "Deep Purple", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

{ label: "🟦 5599FF", insertText: "5599FF", detail: "Pastel Blue", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟦 00AAFF", insertText: "00AAFF", detail: "Bright Blue", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟦 00ABFF", insertText: "00ABFF", detail: "Light Blue", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟦 00DCFF", insertText: "00DCFF", detail: "Sky Blue", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟦 00FFFF", insertText: "00FFFF", detail: "Cyan", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

{ label: "🟩 77FF99", insertText: "77FF99", detail: "Soft Green", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟩 34FF43", insertText: "34FF43", detail: "Vibrant Green", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟩 00FF00", insertText: "00FF00", detail: "Neon Green", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },
{ label: "🟩 00BB00", insertText: "00BB00", detail: "Lime Green", kind: monacoEditor.languages.CompletionItemKind.Color, sort: "0" },

  ];

  window.myCompletionProvider = monacoEditor.languages.registerCompletionItemProvider("myCustomLanguage", {
    provideCompletionItems: function (model, position) {
      const editor = editorRef.current;
      if (!editor) {
        console.warn("Editor is null in provideCompletionItems");
        return { suggestions: [] };
      }
  
      const selection = editor.getSelection();
      const lineText = model.getLineContent(position.lineNumber);
      const fontColorMatch = lineText.match(/<font\s+color="([^"]*)"?/);
  
      // Translation suggestions for decorated lines with selection
      if (selection && !selection.isEmpty()) {
        const startLine = selection.startLineNumber;
        const isDecorated = editor.getLineDecorations(startLine)?.length > 0;
        if (isDecorated && currentTranslationSuggestions.length > 0) {
          const suggestionsWithRange = currentTranslationSuggestions.map((item) => ({
            ...item,
            range: selection,
            sortText: item.label, // Ensure consistent sorting
          }));
          return { suggestions: suggestionsWithRange }; // Exit here
        }
      }
  
      // Color suggestions for <font color="...">
      if (fontColorMatch && position.column >= fontColorMatch.index + 13) {
        return { suggestions: colorSuggestions }; // Exit here
      }
  
      // Default snippet logic (only if no other conditions met)
      // Get the text before the cursor to determine the prefix
      const textBeforeCursor = lineText.substring(0, position.column - 1);
      // Find the last word-like segment before the cursor, including symbols like . and :
      const lastWordMatch = textBeforeCursor.match(/[\w\.\:]+$/);
      const prefix = lastWordMatch ? lastWordMatch[0] : "";
      const startColumn = lastWordMatch ? position.column - 1 - prefix.length : position.column - 1;

      // Define the range for the suggestion (the text to replace)
      const range = new monacoEditor.Range(
        position.lineNumber,
        startColumn + 1, // Start of the prefix
        position.lineNumber,
        position.column // End at the cursor
      );

      let updatedSuggestions = suggestions
        .filter((item) => {
          // Filter suggestions based on the prefix, case-insensitive
          return item.label.toLowerCase().startsWith(prefix.toLowerCase());
        })
        .map((item) => {
          // $sN variables escaped, each "$" placeholder a tab stop (see toTermSnippet).
          const snippetText = toTermSnippet(item.insertText);

          return {
            ...item,
            range: range, // Set the range to the prefix
            insertText: snippetText,
            insertTextRules: monacoEditor.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            filterText: item.label, // Ensure filterText matches the label
          };
        });
  
      updatedSuggestions.push({
        label: "<font color=\"\">",
        insertText: "<font color=\"\">$</font>",
        kind: monacoEditor.languages.CompletionItemKind.Snippet,
        insertTextRules: monacoEditor.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: range, // Apply the same range to the font color snippet
      });
  
      return { suggestions: updatedSuggestions };
    },
  });
  
  return () => {
    if (window.myCompletionProvider) {
      window.myCompletionProvider.dispose();
    }
  };
  }, [monacoEditor, suggestions, currentTranslationSuggestions]); // Depend on `suggestions` to force update

  /*useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "v") {
        handlePaste(e);
      } else if (e.ctrlKey && e.key === "c") {
        handleCopy();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);*/

  // Handle search type selection
  const handleSearchTypeChange = (type) => {
    setSearchType(type.target.value);
    switch (type.target.value) {
      case "skill":
        setPrefixType("desc");
        break;
      case "item":
        setPrefixType("description");
        break;
      case "string":
        setPrefixType("string");
        break;
      case "name":
        setPrefixType("name");
        break;
      default:
        break;
    }
  };

    // Call handleLangTypeChange when the language type is changed
    useEffect(() => {
        setSuggestions([]);
        requestTerms();
    }, [langType]);

    const requestTerms = () => {
        if (isSocketReady) {
            socket.emit("reqTerms", { lang: langType });
        }
    }

  // Handle input paste event
  const handlePaste = (e) => {
    e.preventDefault();
  
    // Always prefer event clipboard data
    let clipboardText = e.clipboardData?.getData("text") || "";
  
    if (!clipboardText) return;
  
    // Apply your formatting rules
    if (clipboardText.includes("[")) {
      let prefix = "";
      if (clipboardText.includes("=[")) {
        prefix = clipboardText.substring(0, clipboardText.indexOf("=[")).trim();
  
        switch (prefix) {
          case "desc":
            setSearchType("skill");
            break;
          case "description":
            setSearchType("item");
            break;
          case "string":
            setSearchType("string");
            break;
          case "name":
            setSearchType("name");
            break;
          default:
            setSearchType("other");
            break;
        }
  
        setPrefixType(prefix);
      }
  
      clipboardText = clipboardText.substring(
        clipboardText.indexOf("[") + 1,
        clipboardText.indexOf("]")
      );
    }
  
    // Replace escaped newlines with real newlines
    clipboardText = clipboardText.replace(/\\n/g, "\n");
  
    // Update both textarea + Monaco state
    setInputValue(clipboardText);
    setOutputValue(clipboardText);
    setRawValue(clipboardText);
  
    handleInputChange({ target: { value: clipboardText } });
  };

  // Flush the server's term cache so terms edited directly in the DB are used
  // immediately by auto-translation and the inline suggestions.
  const clearTermCache = () => {
    if (!isSocketReady || !socket) {
      toast.error("No server connection.");
      return;
    }
    socket.emit("clearTranslationCache", {}, (resp) => {
      if (resp && resp.ok) toast.success("Term cache cleared — edited terms are live.");
      else toast.error("Could not clear the term cache.");
    });
  };

  const handleTrans = async (lang) => {
    handleCloseContextMenu();
    if (!isSocketReady) {
      toast.error("No server connection.");
      return;
    }

    if (editorRef.current.getValue().length === 0) {
      toast.error("No text to translate.");
      return;
    }

    // Show context menu to select target language
    //const lang = prompt("Enter target language (en, ru, kr):", "ru");
    if (!lang) {
      return;
    }

    // Emit the translation request
    socket.emit("translate", {
      text: editorRef.current.getValue(),
      lang,
    });

    // Listen for the translation response
    socket.once("translation", ({ data }) => {
      if (data.error) {
        toast.error("Translation error: " + data.error);
        return;
      }
    
      //console.log("Received translation data:", data);
    
      // Normalize line endings in the received text
      const normalizedText = data.text.replace(/\r\n/g, '\n');
      //console.log("Normalized text:", normalizedText);
    
      // Set editor content with normalized text
      editorRef.current.setValue(normalizedText);
    
      // Clear all decorations
      const previousDecorations = editorRef.current.deltaDecorations([], []);
      //console.log("Cleared previous decorations:", previousDecorations);
    
      // Create decorations for untranslated lines
      const decorations = (data.noTrans || []).map(lineNumber => {
        //console.log(`Adding decoration for line ${lineNumber}`);
        return {
          range: new monacoEditor.Range(lineNumber, 1, lineNumber, 1),
          options: {
            isWholeLine: true,
            className: 'untranslated-line',
            hoverMessage: { value: 'This line was not translated' }
          }
        };
      });
    
      // Apply decorations
      const newDecorationIds = editorRef.current.deltaDecorations([], decorations);
      //console.log("Applied new decorations:", newDecorationIds);
    
      // Update state with normalized text
      setOutputValue(normalizedText);
      setRawValue(normalizedText);
      handleInputChange({ target: { value: normalizedText } });
      toast.success("Translation successful!");
    
      //console.log("Untranslated lines:", data.noTrans);
    });
  };

  // Handle copy event
  const handleCopy = () => {
    let formattedOutput = "";

    //console.log(document.activeElement.id);

    // If #inp is focused:
    if (document.activeElement.id === "inp") {
      formattedOutput = inputValue;
    } else {
      // Check if there's a selection in the Monaco Editor
      let fullDesc = false;
      const selection = editorRef.current.getSelection();
      formattedOutput = editorRef.current
        .getModel()
        .getValueInRange(selection);

      if (formattedOutput.length == 0 || formattedOutput.length === editorRef.current.getValue().length) {
        formattedOutput = editorRef.current.getValue();
        fullDesc = true;
      }

      if (formattedOutput.length == 0) {
        return;
      }

      if (searchTypeRef.current === "skill") {
        // Replace all %% with %
        formattedOutput = formattedOutput.replace(/%%/g, "%");
        // Then replace all single % with %%
        formattedOutput = formattedOutput.replace(/%/g, "%%");
      } else {
        // Non-skill modes use a single %; collapse any %% so the doubled
        // form is only ever produced in Skill mode.
        formattedOutput = formattedOutput.replace(/%%/g, "%");
      }

      if (fullDesc && usePrefixRef.current && prefixRef.current.length > 0) {
        formattedOutput = `${prefixRef.current}=[${formattedOutput}]`;
      }

      // Only process line joining if it's the full content
      if (fullDesc) {
        let finalLines = [];
        formattedOutput = formattedOutput.replace(/\n/g, "\\n");
        // Iterate every line and trim it
        formattedOutput.split("\\n").forEach((line, index) => {
          finalLines.push(line.trim());
        });
        formattedOutput = finalLines.join("\\n");
      }
      // For partial selections, we'll keep the original line breaks
      // without converting to \n

      if (formattedOutput.length === 0) {
        return;
      }

      navigator.clipboard.writeText(formattedOutput);
      toast.success("Copied to clipboard!");
    }
  };

  // Function to preview formatted output
  const doPreview = (text) => {
    text = text.replace(
      /<font color=("|')?([^"'>\s]+)\1?>([\s\S]*?)(<\/font>|$)/gi,
      (match, p1, color, content) => {
        let closingTag = match.includes("</font>") ? "</font>" : "";
        return `?${color}?${content}?!!!!!`;
      }
    );

    text = text.replace(/<(?!\?)|(?<!\?)>/g, (match) =>
      match === "<" ? "&lt;" : "&gt;"
    );
    text = text.replace(/\n/g, "<br>");
    text = text.replace(
      /\?([^\?]+)\?([\s\S]*?)\?!!!!!/g,
      '<font color="$1">$2</font>'
    );

    return text;
  };

  // Handle input change and trigger preview update
  const handleInputChange = (e) => {
    let text = e.target.value;
    setOutputValue(text);
    setRawValue(text); // Update rawValue for Monaco Editor
    text = doPreview(text);

    if (searchType === "skill") {
      text = text.replace(/%%/g, "%");
    }

    // Sanitize before it reaches dangerouslySetInnerHTML. Allow only the tags the
    // preview actually produces (font color, line breaks) so injected markup like
    // <img onerror=...> is stripped.
    const safeText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ["font", "br", "span", "b", "i", "strong", "em"],
      ALLOWED_ATTR: ["color", "style", "class"],
    });

    document.getElementById("preview").classList.remove("l2");
    document.getElementById("preview").classList.remove("ru");

    if (/[а-яА-ЯЁё]/.test(text)) {
      setPreview(safeText);
      document.getElementById("preview").classList.add("ru");
    } else {
      setPreview(safeText);
      document.getElementById("preview").classList.add("l2");
    }

    editorRef.current.deltaDecorations([], []);
  };

  // Handle search with debounce
  const handleSearch = (e) => {
    if (timer) clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        handleInputChange(e);
      }, 500)
    );
  };

  // Handle Monaco Editor changes
  const handleEditorChange = (value, event) => {
    setOutputValue(value); // Update outputValue
    setRawValue(value); // Update rawValue
    handleInputChange({ target: { value } }); // Trigger preview update
    // If editor is empty, clear all decoration
  };

  const debouncedFetchTranslation = useCallback((text, editor, monaco) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchTranslation(text, editor, monaco);
    }, 500); // 300ms delay (adjust as needed)
  }, [socket, isSocketReady, toast]); // Dependencies for useCallback

  const fetchTranslation = (text, editor, monaco) => {
    if (!socket || !isSocketReady) {
      console.error("Socket is not ready yet.");
      toast.error("Translation unavailable: Socket not connected.");
      return;
    }

    socket.emit("translate-single", { text, outLang: "en" });
    socket.once("translation-single", ({ data }) => {
      //console.log("Server response:", data);
      if (data.error) {
        console.error("Translation error:", data.error);
        toast.error(`Translation error: ${data.error}`);
        return;
      }
      if (!data || typeof data.translation !== "string") {
        console.error("Invalid translation data:", data);
        toast.error("Invalid translation response from server.");
        return;
      }

      // If the translation is the same as the original text, skip suggestions
      if (data.translation === text) {
        //console.log("Translation is the same as the original text.");
        setcurrentTranslationSuggestions([]);
        return;
      }

      // Update suggestions state
      const suggestions = [
        {
          label: `Translation: ${data.translation}`,
          kind: monaco.languages.CompletionItemKind.Text,
          insertText: data.translation,
        },
      ];
      if (Array.isArray(data.suggestions)) {
        suggestions.push(
          ...data.suggestions
            .filter((sug) => typeof sug === "string" && sug.length > 0)
            .map((sug) => ({
              label: sug,
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: sug,
            }))
        );
      }
      setcurrentTranslationSuggestions(suggestions);
      //console.log("Translation suggestions:", suggestions);
      editor.trigger("manual", "editor.action.triggerSuggest", {});
    });
  };

  const showSuggestions = (data, editor, monaco) => {
    monaco.languages.registerCompletionItemProvider("myCustomLanguage", {
      provideCompletionItems: (model, position) => {
        if (data.noTrans !== undefined && data.noTrans.length > 0) {
          return { suggestions: [] };
        }
        const selection = editor.getSelection();
        if (selection !== null && !selection.isEmpty()) {
          const suggestions = [
            {
              label: `Translation: ${data.text}`,
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: data.text || "", // Fallback to empty string
              range: selection,
            },
          ];

          if (Array.isArray(data.text)) {
            const validSuggestions = data.text
              .filter((sug) => typeof sug === "string" && sug.length > 0) // Only valid strings
              .map((sug) => ({
                label: sug,
                kind: monaco.languages.CompletionItemKind.Text,
                insertText: sug,
                range: selection,
              }));
            suggestions.push(...validSuggestions);
          } else if (data.text !== undefined) {
            console.warn("data.suggestions is not an array:", data.text);
          }

          //console.log("Suggestions provided:", suggestions); // Debug: Log suggestions
          return { suggestions };
        }
        return { suggestions: [] };
      },
    });
    editor.trigger("manual", "editor.action.triggerSuggest", {});
  };

  const isLineDecorated = (lineNumber, editor) => {
    const decorations = editor.getLineDecorations(lineNumber);
    return decorations.length > 0; // Returns true if the line has decorations
  };

  // Initialize Monaco Editor
  const handleEditorDidMount = (editor, monaco) => {
    setMonacoEditor(monaco);
    editorRef.current = editor;
    // Register the custom language
    monaco.languages.register({ id: "myCustomLanguage" });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyC, () => {
      handleCopy(); // your existing function
    });

    // Set up a simple tokenizer (optional)
    monaco.languages.setMonarchTokensProvider("myCustomLanguage", {
      tokenizer: {
        root: [
          [/\d+/, "variable"], // Example: Highlight numbers
          [/%/, "variable"], // Example: Highlight special characters like %%
          [/\+/g, "variable"], // Example: Highlight special characters like +
          [/\-/, "variable"], // Example: Highlight special characters like [text]
          // Highlight HTML tags
          [/<[a-zA-Z0-9]+\s[a-zA-Z0-9]+=["]{0,1}[a-zA-Z0-9]+["]{0,1}\>/, "tag"],
          [/<\/[a-zA-Z0-9]+>/, "tag"],
          [/\$s[0-9]+/, "variable"], // Example: Highlight variables like $s123
        ],
      },
    });

    // Inline term suggestions: when foreign (Korean/Russian) text is selected,
    // show a floating box of term-memory candidates near the selection.
    editor.onDidChangeCursorSelection(() => {
      const selection = editor.getSelection();
      const selectedText = editor.getModel().getValueInRange(selection);

      const src = detectLang(selectedText);
      const out = OUT_CODE[langTypeRef.current] || "EU";
      // Only offer suggestions when the selection is in a language different from
      // the chosen output language (e.g. KR selection with EN output, or EN with RU).
      if (!selectedText || !src || src === out) {
        setSuggestBox(null);
        return;
      }

      const dom = editor.getDomNode();
      const pos = editor.getScrolledVisiblePosition(selection.getStartPosition());
      if (!dom || !pos) {
        setSuggestBox(null);
        return;
      }
      const rect = dom.getBoundingClientRect();
      const top = Math.min(rect.top + pos.top + (pos.height || 19) + 2, window.innerHeight - 260);
      const left = Math.min(rect.left + pos.left, window.innerWidth - 360);
      const range = selection;
      const text = selectedText;
      setSuggestBox({ top, left, loading: true, items: [], range });

      clearTimeout(suggestTimerRef.current);
      suggestTimerRef.current = setTimeout(() => startInlineLookup(text), 300);
    });

    // Reposition/hide the box when the editor scrolls.
    editor.onDidScrollChange(() => setSuggestBox(null));
  };

  // Look up line suggestions across BOTH items and skills for the selected text.
  const startInlineLookup = (text) => {
    const sock = socketRef.current;
    if (!sock || !sock.connected) {
      setSuggestBox((b) => (b ? { ...b, loading: false, items: [] } : b));
      return;
    }
    const srcLang = detectLang(text) || "EU";
    const outLang = OUT_CODE[langTypeRef.current] || "EU";
    if (srcLang === outLang) {
      setSuggestBox((b) => (b ? { ...b, loading: false, items: [] } : b));
      return;
    }
    const vers = versionsRef.current;
    const ver = vers && vers.length ? Math.max(...vers.map((v) => v.id)) : 474;
    const reqId = ++lookupIdRef.current;
    let settled = false;
    const finish = (patch) => {
      if (settled) return;
      settled = true;
      if (reqId !== lookupIdRef.current) return; // a newer selection superseded this
      setSuggestBox((b) => (b ? { ...b, loading: false, items: [], ...patch } : b));
    };
    // Backstop: if the server never acks (not restarted with suggestLines, or a
    // slow unindexed scan), don't leave the box stuck on "Searching" forever.
    const timer = setTimeout(
      () => finish({ error: "Timed out. Is the server running the latest build?" }),
      12000
    );
    sock.emit("suggestLines", { text, inLang: srcLang, outLang, ver }, (resp) => {
      clearTimeout(timer);
      if (resp && resp.error) {
        finish({ error: "Lookup failed on the server." });
        return;
      }
      finish({ items: (resp && resp.suggestions) || [] });
    });
  };

  // Replace the current editor selection with a chosen suggestion AND save the
  // src->dest pair to the term memory.
  const applySuggestion = (item) => {
    const editor = editorRef.current;
    if (editor && suggestBox) {
      editor.executeEdits("suggest", [
        { range: suggestBox.range, text: item.dest, forceMoveMarkers: true },
      ]);
      editor.focus();
    }
    const sock = socketRef.current;
    if (sock && sock.connected) {
      const language = /[가-힣]/.test(item.src)
        ? "Korean"
        : /[а-яА-Я]/.test(item.src)
        ? "Russian"
        : "English";
      const resultLang = OUT_NAME[langTypeRef.current] || "English";
      sock.emit(
        "copyTerm",
        { src: item.src, term: item.dest, language, resultLang },
        (resp) => {
          if (resp && resp.ok) {
            toast.success(
              resp.variant
                ? "Saved as an alternate phrasing of an existing term"
                : "Saved to term memory"
            );
          }
        }
      );
    }
    setSuggestBox(null);
  };

  // Dismiss the suggestion box on outside click or Escape.
  useEffect(() => {
    if (!suggestBox) return;
    const onDown = (e) => {
      if (!e.target.closest(".tt-suggest")) setSuggestBox(null);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setSuggestBox(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [suggestBox]);

  return (
    <div className="App">
      <h1>L2 Localization Tool</h1>
      <div className="search-container">
        <NavBar
          username={username}
          onLogout={logout}
          updateDatabase={updateDatabase}
        />
        <center>
          <h1>Translation Helper</h1>
          <strong>
            <h3>Tool mode &nbsp;&nbsp;&nbsp; Language</h3>
          </strong>
          <select
            name="typeselect"
            id="typeselect"
            style={{ width: "100px" }}
            onChange={handleSearchTypeChange}
          >
            <option value="skill">Skill</option>
            <option value="item">Item</option>
            <option value="string">Sys/NpcString</option>
            <option value="other" disabled>Other</option>
          </select>
          &nbsp;&nbsp;&nbsp;
          <select
            name="langselect"
            id="langselect"
            style={{ width: "100px" }}
            onChange={(e) => setLangType(e.target.value)}
            value={langType}
          >
            <option value="en">English</option>
            <option value="ru">Russian</option>
            <option value="kr">Korean</option>
          </select>
          <br />
          <label>
          <input type="checkbox" id="usePrefix" onChange={(e) => setUsePrefix(e.target.checked)} checked={usePrefix} /> Use prefix
          {/* If usePrefix is on and prefixType is not null, show it*/}
          {usePrefix && prefixType && <span> ({prefixType})</span>}
          </label>
          <br />
          <button
            type="button"
            onClick={clearTermCache}
            title="Flush the server's term cache so terms you edited directly in the DB are used right away"
          >
            ↻ Refresh term cache
          </button>
        </center>
        <div className="search-field-container">
          <table>
            <thead>
              <tr>
                <th>
                  <label>Input</label>
                </th>
                <th>
                  <label>Output</label>
                </th>
                <th>
                  <label>Preview</label>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <textarea
                    className="toolArea"
                    id="inp"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onPaste={handlePaste}
                    cols={80}
                    rows={20}
                  />
                  <center>
                    <button id="copy" onClick={handlePaste}>
                      Paste from clipboard
                    </button>
                  </center>
                </td>
                <td>
                  {!isSocketReady ? (<div>Loading editor...</div>) : (
                  <Editor
                    height="557px"
                    width="541px"
                    language="myCustomLanguage" // Use "plaintext" or a custom language
                    theme="vs-dark" // Use "vs", "vs-dark", or "hc-black"
                    value={rawValue}
                    onChange={handleEditorChange}
                    onMount={handleEditorDidMount}
                    options={{
                      trimAutoWhitespace: true,
                      fontFamily: "sans-serif",
                      fontSize: 16,
                      wordWrap: "on",
                      minimap: { enabled: false },
                      lineNumbers: "off",
                      glyphMargin: false,
                      folding: false,
                      lineDecorationsWidth: 0,
                      lineNumbersMinChars: 1,
                      //scrollbar: { vertical: 'hidden', horizontal: 'hidden' },
                      scrollBeyondLastColumn: 0,
                      scrollBeyondLastLine: false,
                      suggest: { showStatusBar: true, showIcons: true },
                      lineHeight: 23,
                      theme: "vs-dark",
                      unicodeHighlight: {
                        ambiguousCharacters: false,
                        invisibleCharacters: true,
                      }
                    }}
                  />
                  )}
                  <center>
                  <button id="copy" onClick={handleContextMenu}>
                      Auto-translate
                    </button>
                    {contextMenu && (
                    <div
                      className="context-menu"
                      style={{
                        top: `${contextMenu.y}px`,
                        left: `${contextMenu.x}px`,
                        width: "100px",
                      }}
                    >
                      {<ul className="context-menu-list">
                        <li className="menuli" onClick={() => handleTrans('en')}>English</li>
                        <li className="menuli" onClick={() => handleTrans('ru')}>Russian</li>
                        <li className="menuli" onClick={() => handleTrans('kr')}>Korean</li>
                      </ul>}
                    </div>
                  )} 
                    <button id="copy" onClick={handleCopy}>
                      Copy to clipboard
                    </button>
                  </center>
                </td>
                <td colSpan="2" style={{ display: "block", border: "none" }}>
                  <div
                    id="preview"
                    dangerouslySetInnerHTML={{ __html: preview }}
                  ></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {suggestBox && (
        <div className="tt-suggest" style={{ top: suggestBox.top, left: suggestBox.left }}>
          <div className="tt-suggest-head">Suggestions</div>
          {suggestBox.loading ? (
            <div className="tt-suggest-muted">Searching&hellip;</div>
          ) : suggestBox.error ? (
            <div className="tt-suggest-muted">{suggestBox.error}</div>
          ) : suggestBox.items.length === 0 ? (
            <div className="tt-suggest-muted">No suggestions.</div>
          ) : (
            <div className="tt-suggest-list">
              {suggestBox.items.map((s, i) => (
                <button
                  key={i}
                  className="tt-suggest-item"
                  onClick={() => applySuggestion(s)}
                  title={`Replace with "${s.dest}" and save to terms`}
                >
                  <span className="tt-suggest-dest">{s.dest}</span>
                  <span className="tt-suggest-src">{s.src}</span>
                  {s.count ? <span className="tt-suggest-count">{s.count}</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransTool;
